import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../api';
import { Calendar, Clock, Euro, ChevronRight, Heart, Gift as GiftIcon } from 'lucide-react';
import BookingConfirmation from '../components/booking/BookingConfirmation';
import TimeSlotSelector from '../components/reservation/TimeSlotSelector';
import Button from '../components/ui/Button';
import { useServiceCategories } from '../hooks/useServiceCategories';
import { useServicesByCategory } from '../hooks/useServices';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuestionnaire, useQuestionnaireValidation } from '../hooks/useQuestionnaires';
import QuestionnaireForm from '../components/questionnaire/QuestionnaireForm';
import QuestionnaireResult from '../components/questionnaire/QuestionnaireResult';
import { giftVoucherService } from '../services/giftVoucherService';
import PaymentSection from '../components/payments/PaymentSection';
import { paymentService } from '../services/paymentService';
import { promoCodeService } from '../services/promoCodeService';
import { stripePromise } from '../lib/stripe';
import { scrollAndFocus } from '../utils/scroll';
import { usePaymentCleanup } from '../hooks/usePaymentCleanup';
import SEOHead from '../components/SEOHead';
const reservationSchema = z.object({
  service: z.string().min(1, "Veuillez choisir un service"),
  datetime: z.date({
    required_error: "Veuillez choisir une date et un horaire",
    invalid_type_error: "Format de date invalide",
  }),
  firstName: z.string().min(2, "Le prénom doit contenir au moins 2 caractères"),
  lastName: z.string().min(2, "Le nom doit contenir au moins 2 caractères"),
  email: z.string().email("Adresse email invalide"),
  phone: z.string().regex(/^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/, "Numéro de téléphone invalide"),
  address_line1: z.string().min(1, "L'adresse est obligatoire"),
  address_line2: z.string().optional(),
  postal_code: z.string().regex(/^\d{5}$/, "Code postal invalide (5 chiffres)"),
  city: z.string().min(1, "La ville est obligatoire"),
  country: z.literal("France"),
  notes: z.string().optional(),
  pregnancy_week: z.string().optional(),
  baby_age: z.string().optional(),
  gift_voucher_number: z.string().optional(),
  terms: z.literal(true, {
    errorMap: () => ({ message: "Vous devez accepter les conditions générales" })
  })
});

type ReservationForm = z.infer<typeof reservationSchema>;

type ReservationStep = 'service' | 'questionnaire' | 'questionnaire-result' | 'booking' | 'payment' | 'payment-processing' | 'confirmation';

const Reservation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [selectedCategorySlug, setSelectedCategorySlug] = useState<string | null>(null);
  const [showServices, setShowServices] = useState(false);
  const [currentStep, setCurrentStep] = useState<ReservationStep>('service');
  const [tempBookingId, setTempBookingId] = useState<number | null>(null);
  const [finalBooking, setFinalBooking] = useState<any>(null);
  const [useGiftVoucherFlag, setUseGiftVoucher] = useState(false);
  const [giftVoucherNumber, setGiftVoucherNumber] = useState('');
  const [formData, setFormData] = useState<ReservationForm | null>(null);
  const [preselectedServiceId, setPreselectedServiceId] = useState<string | null>(null);
  const [giftVoucherInfo, setGiftVoucherInfo] = useState<any>(null);
  const [giftVoucherLoading, setGiftVoucherLoading] = useState(false);
  const [giftVoucherError, setGiftVoucherError] = useState<string | null>(null);
  const [isGiftVoucherExpanded, setIsGiftVoucherExpanded] = useState(false);
  const [categoryUpdateKey, setCategoryUpdateKey] = useState(0);
  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoDiscount, setPromoDiscount] = useState<number | null>(null);
  const [promoValidCode, setPromoValidCode] = useState<string | null>(null);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [paymentClientSecret, setPaymentClientSecret] = useState<string | null>(null);
  const [paymentAmountCents, setPaymentAmountCents] = useState<number | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [isProcessingBooking, setIsProcessingBooking] = useState(false);
  // Synchronously check for post-payment redirect to prevent UI flash
  const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const isPostPaymentRedirect = params.has('payment_intent') && params.has('redirect_status');
  const [isConfirmingPayment, setIsConfirmingPayment] = useState(isPostPaymentRedirect);

  // Extraire le PaymentIntent ID du clientSecret (format: pi_xxx_secret_yyy)
  const paymentIntentId = paymentClientSecret ? paymentClientSecret.split('_secret_')[0] : null;
  
  // Hook pour annuler automatiquement le PaymentIntent si l'utilisateur quitte la page
  // Disabled in dev: React StrictMode double-mount triggers premature cancellation
  usePaymentCleanup({
    paymentIntentId,
    isPaymentComplete: currentStep === 'confirmation' || currentStep === 'payment-processing',
    enabled: !import.meta.env.DEV && currentStep === 'payment',
  });

  // Gérer le retour de paiement Stripe
  React.useEffect(() => {
    if (!isConfirmingPayment) return;

    const handlePaymentReturn = async () => {
      const params = new URLSearchParams(window.location.search);
      const paymentIntentId = params.get('payment_intent');

      try {
        // On ne fait rien si l'ID n'est pas là, l'état isConfirmingPayment gère déjà l'UI
        if (!paymentIntentId) throw new Error('Payment Intent ID manquant');

        const response = await fetch(`${API_BASE_URL}/api/payments/intent/${paymentIntentId}`);
        if (!response.ok) throw new Error('Erreur lors de la récupération des détails du paiement');
        
        const paymentIntent = await response.json();
        const bookingId = paymentIntent.metadata?.booking_id;
        
        if (bookingId) {
          // Marquer la réservation comme payée côté serveur (confirmation sans webhook)
          try {
            await paymentService.markBookingPaid(parseInt(bookingId, 10), paymentIntentId);
          } catch (e) {
            console.warn('markBookingPaid échoué (webhook le fera):', e);
          }
          // Finaliser la réservation SANS l'option showProcessing, car l'UI est déjà gérée
          await createFinalBookingWithId(parseInt(bookingId, 10));
        } else {
          throw new Error('ID de réservation manquant dans les métadonnées du paiement');
        }
      } catch (error) {
        console.error('Erreur lors de la finalisation du paiement:', error);
        const message = error instanceof Error ? error.message : 'Une erreur est survenue.';
        setPaymentError(message);
        // En cas d'erreur, on arrête le chargement et on retourne à l'étape de paiement
        setCurrentStep('payment'); 
      } finally {
        // Quoi qu'il arrive, on arrête l'état de confirmation de paiement
        setIsConfirmingPayment(false);
        // Nettoyer l'URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    };

    handlePaymentReturn();
  }, [isConfirmingPayment]);

  // Scroll automatique vers la section pertinente lors des changements d'étape
  React.useEffect(() => {
    let selector: string | null = null;
    if (currentStep === 'questionnaire-result') selector = '[data-section="questionnaire-result"]';
    if (currentStep === 'confirmation') selector = '[data-section="confirmation"]';

    if (!selector) return;

    const tryScroll = () => {
      const el = document.querySelector(selector!);
      if (el && 'scrollIntoView' in el) {
        (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
      } else {
        // Réessayer au prochain frame si le DOM n'est pas prêt
        requestAnimationFrame(tryScroll);
      }
    };

    // Donner un léger délai pour s'assurer que le DOM a rendu la section
    setTimeout(() => requestAnimationFrame(tryScroll), 50);
  }, [currentStep]);

  // Fetch categories and services from API
  const { categories, loading: categoriesLoading } = useServiceCategories();
  const { services, loading: servicesLoading } = useServicesByCategory(
    selectedCategorySlug || '',
    { bookableOnly: true },
  );

  // Gestion des paramètres URL pour pré-sélection de service et expansion bon cadeau
  React.useEffect(() => {
    const serviceParam = searchParams.get('service');
    const categoryParam = searchParams.get('category');
    const expandGiftVoucher = searchParams.get('expand_gift_voucher');

    // Déplier l'encart bon cadeau si demandé
    if (expandGiftVoucher === 'true') {
      setIsGiftVoucherExpanded(true);
    }

    // Si on a service + catégorie dans l'URL, appliquer immédiatement, sans attendre les catégories
    if (serviceParam && categoryParam) {
      setSelectedCategorySlug(categoryParam);
      setShowServices(true);
      setPreselectedServiceId(serviceParam);
      return;
    }

    if (serviceParam && categories.length > 0) {
      setSelectedCategorySlug('');
      setShowServices(false);
      setPreselectedServiceId(null);

      const findServiceCategory = async () => {
        let foundCategory: string | null = null;
        let foundService: any = null;

        for (const category of categories) {
          try {
            const categoryServices = await fetch(`${API_BASE_URL}/api/services?category=${category.slug}`).then(r => r.json());
            const service = categoryServices.find((s: any) => s.id.toString() === serviceParam);
            if (service) {
              foundCategory = category.slug;
              foundService = service;
              break;
            }
          } catch (error) {
            console.error('Error checking category:', category.slug, error);
          }
        }

        if (foundCategory && foundService) {
          setTimeout(() => {
            setSelectedCategorySlug(foundCategory);
            setShowServices(true);
            setCategoryUpdateKey(prev => prev + 1);
            setTimeout(() => {
              setPreselectedServiceId(serviceParam);
            }, 500);
          }, 100);
        }
      };

      findServiceCategory();
    }
  }, [searchParams, categories]);

  // Post-redirect Stripe handling: if redirected back with client_secret, retrieve PI and finalize
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const clientSecret = params.get('payment_intent_client_secret');
    if (!clientSecret) return;
    (async () => {
      try {
        const stripe = await stripePromise;
        if (!stripe) return;
        const { paymentIntent, error } = await stripe.retrievePaymentIntent(clientSecret);
        if (error || !paymentIntent) return;
        const status = paymentIntent.status;
        const type = (paymentIntent.metadata && (paymentIntent.metadata as any).type) || undefined;
        if ((status === 'succeeded' || status === 'processing' || status === 'requires_capture') && type === 'booking') {
          const bookingIdStr = (paymentIntent.metadata as any).booking_id;
          const bookingId = bookingIdStr ? parseInt(bookingIdStr, 10) : undefined;
          if (bookingId) {
            try {
              await paymentService.markBookingPaid(bookingId, paymentIntent.id);
            } catch (e) {
              console.warn('markBookingPaid échoué (webhook le fera):', e);
            }
            await createFinalBookingWithId(bookingId);
          }
        }
      } catch (e) {
        console.error('Erreur post-redirection Stripe:', e);
      }
    })();
  }, []);

  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<ReservationForm>({
    resolver: zodResolver(reservationSchema)
  });

  // Pré-sélectionner le service quand il est trouvé
  React.useEffect(() => {
    if (preselectedServiceId && services.length > 0 && !servicesLoading && selectedCategorySlug) {
      const foundService = services.find(s => s.id.toString() === preselectedServiceId);
      if (foundService) {
        setValue('service', preselectedServiceId);
        setTimeout(() => {
          handleServiceSelect();
        }, 500);
      }
    }
  }, [preselectedServiceId, services, servicesLoading, selectedCategorySlug, setValue]);

  const selectedService = watch('service');
  const selectedServiceData = services.find(s => s.id.toString() === selectedService);
  
  // Utiliser les données du bon cadeau si le service n'est pas trouvé dans la liste normale
  const effectiveServiceData = selectedServiceData || (giftVoucherInfo ? {
    id: selectedService || giftVoucherInfo.id,
    name: giftVoucherInfo.name,
    duration_minutes: giftVoucherInfo.duration_minutes,
    price: giftVoucherInfo.price || giftVoucherInfo.price_cents,
    category: { 
      name: giftVoucherInfo.category?.name || giftVoucherInfo.category_name,
      slug: giftVoucherInfo.category?.slug || giftVoucherInfo.category_slug || 'unknown'
    }
  } : null);
  
  // Service data tracking removed for production

  // Forcer le re-render quand un service est sélectionné
  const [forceUpdate, setForceUpdate] = React.useState(0);
  React.useEffect(() => {
    if (selectedService) {
      setForceUpdate(prev => prev + 1);
    }
  }, [selectedService]);

  // Hooks pour les nouvelles fonctionnalités
  const { questionnaire } = useQuestionnaire(effectiveServiceData?.id, effectiveServiceData?.name);
  const [ariaMessage, setAriaMessage] = useState('');
  const { validation } = useQuestionnaireValidation();
  const [localValidation, setLocalValidation] = useState<any>(null);

  const handleCategorySelect = (categorySlug: string) => {
    setSelectedCategorySlug(categorySlug);
    setShowServices(true);
    setTimeout(() => {
      const el = document.querySelector('[data-section="services"]');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleGiftVoucherCheck = async () => {
    if (!giftVoucherNumber.trim()) return;

    setGiftVoucherLoading(true);
    setGiftVoucherError(null);
    setGiftVoucherInfo(null);

    try {
      const result = await giftVoucherService.getWithService(giftVoucherNumber);

      if (result.found && result.service && result.is_valid) {
        // Pré-remplir le service
        setGiftVoucherInfo(result.service);

        // Trouver la catégorie du service
        for (const category of categories) {
          const categoryServices = await fetch(`${API_BASE_URL}/api/services?category=${category.slug}`).then(r => r.json());
          const foundService = categoryServices.find((s: any) => s.id === result.service!.id);
          if (foundService) {
            setSelectedCategorySlug(category.slug);
            setShowServices(true);
            setValue('service', result.service.id.toString());
            break;
          }
        }

        // Activer le flag du bon cadeau
        setUseGiftVoucher(true);
      } else {
        setGiftVoucherError(result.error || 'Bon cadeau non valide ou expiré.');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Une erreur inconnue est survenue.';
      setGiftVoucherError(errorMessage);
      console.error('Erreur lors de la vérification du bon cadeau:', error);
    } finally {
      setGiftVoucherLoading(false);
    }
  };

  const handleServiceSelect = () => {
    const tryScroll = () => {
      const dateTimeSection = document.querySelector('[data-section="datetime"]') as HTMLElement | null;
      if (dateTimeSection) {
        scrollAndFocus(dateTimeSection, { hash: '#datetime', announce: setAriaMessage, message: 'Étape 2 : Choisissez une date et une heure', focusSelector: 'h2, h3', pushState: true });
      } else {
        requestAnimationFrame(tryScroll);
      }
    };
    setTimeout(() => requestAnimationFrame(tryScroll), 100);
  };

  const handleDateSelected = () => {
    const tryScroll = () => {
      const infoSection = document.querySelector('[data-section="customer"]') as HTMLElement | null;
      if (infoSection) {
        scrollAndFocus(infoSection, { hash: '#infos', announce: setAriaMessage, message: 'Étape 3 : Vos informations', focusSelector: 'input, h3', pushState: true });
      } else {
        requestAnimationFrame(tryScroll);
      }
    };
    setTimeout(() => requestAnimationFrame(tryScroll), 50);
  };

  const onSubmit = async (data: ReservationForm) => {
    try {
      setIsProcessingBooking(true);
      // Vérifier si un questionnaire est requis et non encore rempli
      // Cette vérification doit se faire AVANT toute création de réservation, même avec bon cadeau
      if (questionnaire && currentStep === 'service' && !validation) {
        // Stocker les données du formulaire sans créer de réservation
        setFormData(data);
        setCurrentStep('questionnaire');
        setIsProcessingBooking(false);
        return;
      }

      // Si on arrive ici, soit pas de questionnaire, soit questionnaire validé
      const tempBooking = await createTempBooking(data);
      setTempBookingId(tempBooking.id);

      // Si un bon cadeau est utilisé et valide, pas de paiement nécessaire (déjÃ  appliqué cÃ´té backend via voucher_number)
      if (useGiftVoucherFlag && giftVoucherNumber.trim()) {
        await createFinalBookingWithId(tempBooking.id);
      } else {
        // Initier le paiement Stripe pour cette réservation
        const amount = typeof effectiveServiceData?.price === 'number' ? effectiveServiceData.price : undefined;
        try {
          const resp = await paymentService.createPaymentIntent({
            type: 'booking',
            booking_id: tempBooking.id,
            ...(typeof amount === 'number' ? { amount_cents: amount } : {}),
            customer_email: data.email,
            description: `Réservation #${tempBooking.id}`,
            ...(promoValidCode ? { promo_code: promoValidCode } : {}),
          });
          setPaymentClientSecret(resp.client_secret);
          const displayAmount = (typeof amount === 'number' && promoDiscount) ? Math.max(50, Math.round(amount * (100 - promoDiscount) / 100)) : (typeof amount === 'number' ? amount : null);
          setPaymentAmountCents(displayAmount);
          setCurrentStep('payment');
        } catch (e: any) {
          const message = e?.message || "Erreur lors de l'initialisation du paiement";
          setPaymentError(message);
        }
      }
    } catch (error) {
      console.error('Erreur lors de la réservation:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      setGeneralError(`Une erreur est survenue : ${errorMessage}`);
    } finally {
      setIsProcessingBooking(false);
    }
  };

  const createTempBooking = async (data: ReservationForm) => {
    const year = data.datetime.getFullYear();
    const month = data.datetime.getMonth();
    const day = data.datetime.getDate();
    const hours = data.datetime.getHours();
    const minutes = data.datetime.getMinutes();

    const localISOString = `${year}-${(month + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}T${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;

    const payload = {
      service_id: parseInt(data.service),
      start_time: localISOString,
      customer_name: `${data.firstName} ${data.lastName}`,
      customer_email: data.email,
      customer_phone: data.phone,
      customer_address_line1: data.address_line1,
      customer_address_line2: data.address_line2 || undefined,
      customer_postal_code: data.postal_code,
      customer_city: data.city,
      customer_country: data.country,
      notes: data.notes || '',
      voucher_number: giftVoucherNumber || undefined
    };

    const response = await fetch(`${API_BASE_URL}/api/bookings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Erreur lors de la réservation');
    }

    return response.json();
  };

  const createFinalBooking = async () => {
    if (!tempBookingId) {
      console.error('Aucun ID de réservation temporaire');
      return;
    }

    // Si bon cadeau, aller directement à la confirmation (déjà appliqué côté backend via voucher_number)
    if (useGiftVoucherFlag && giftVoucherNumber.trim()) {
      await createFinalBookingWithId(tempBookingId);
      return;
    }

    // Initier le paiement Stripe pour cette réservation
    const amount = typeof effectiveServiceData?.price === 'number' ? effectiveServiceData.price : undefined;
    try {
      setIsProcessingBooking(true);
      const resp = await paymentService.createPaymentIntent({
        type: 'booking',
        booking_id: tempBookingId,
        ...(typeof amount === 'number' ? { amount_cents: amount } : {}),
        customer_email: formData?.email || '',
        description: `Réservation #${tempBookingId}`,
        ...(promoValidCode ? { promo_code: promoValidCode } : {}),
      });
      setPaymentClientSecret(resp.client_secret);
      const displayAmount = (typeof amount === 'number' && promoDiscount) ? Math.max(50, Math.round(amount * (100 - promoDiscount) / 100)) : (typeof amount === 'number' ? amount : null);
      setPaymentAmountCents(displayAmount);
      setCurrentStep('payment');
    } catch (e: any) {
      const message = e?.message || "Erreur lors de l'initialisation du paiement";
      setPaymentError(message);
    } finally {
      setIsProcessingBooking(false);
    }
  };

  const createFinalBookingWithId = async (
    bookingId: number,
    options: { showProcessing?: boolean; fallbackStep?: ReservationStep } = {}
  ) => {
    const { showProcessing = false, fallbackStep = showProcessing ? 'payment' : 'service' } = options;

    // L'option showProcessing est conservée pour les cas sans redirection (ex: bon cadeau)
    if (showProcessing) {
      setCurrentStep('payment-processing');
      await new Promise(resolve => setTimeout(resolve, 2000)); // Délai pour la visibilité
    }

    try {
      const bookingResponse = await fetch(`${API_BASE_URL}/api/bookings/${bookingId}`);
      const bookingData = await bookingResponse.json();

      if (!bookingResponse.ok) {
        throw new Error(bookingData?.detail || "Impossible de récupérer la réservation confirmée.");
      }

      // Booking data retrieved

      setFinalBooking(bookingData);
      setCurrentStep('confirmation');
    } catch (error) {
      const message = error instanceof Error ? error.message : "Une erreur est survenue lors de la confirmation.";
      console.error('Erreur lors de la récupération du booking confirmé:', error);

      if (fallbackStep === 'payment') {
        setPaymentError(message);
      }

      setGeneralError(message);
      setCurrentStep(fallbackStep);
    } finally {
      setIsProcessingBooking(false);
    }
  };

  const handleQuestionnaireSubmit = async (responses: Record<string, boolean | string>) => {
    if (!formData || !questionnaire) return;

    // Questionnaire validation
    
    try {
      // Valider les réponses localement sans appeler l'API
      // On vérifie juste s'il y a des contre-indications
      const hasContraindications = questionnaire.questions.some(q => 
        q.contraindication && responses[q.id] === true
      );
      
      // Simuler la validation sans appel API
      const mockValidation = {
        is_eligible: !hasContraindications,
        questionnaire_type: questionnaire.questionnaire_type,
        contraindications: questionnaire.questions
          .filter(q => q.contraindication && responses[q.id] === true)
          .map(q => q.text)
      };
      
      // Stocker la validation manuellement
      setLocalValidation(mockValidation);
      
      setCurrentStep('questionnaire-result');
    } catch (error) {
      console.error('Erreur questionnaire:', error);
      setGeneralError('Erreur lors de la validation du questionnaire');
    }
  };

  const handleQuestionnaireResult = async () => {
    const currentValidation = localValidation || validation;
    if (currentValidation?.is_eligible) {
      if (!formData) return;
      setIsProcessingBooking(true);
      try {
        const tempBooking = await createTempBooking(formData);
        setTempBookingId(tempBooking.id);

        // Si un bon cadeau est utilisé et valide, pas de paiement nécessaire (déjÃ  appliqué cÃ´té backend via voucher_number)
        if (useGiftVoucherFlag && giftVoucherNumber.trim()) {
          await createFinalBookingWithId(tempBooking.id);
          return;
        }

        // Initier le paiement Stripe pour cette réservation
        const amount = typeof effectiveServiceData?.price === 'number' ? effectiveServiceData.price : undefined;
        try {
          const resp = await paymentService.createPaymentIntent({
            type: 'booking',
            booking_id: tempBooking.id,
            ...(typeof amount === 'number' ? { amount_cents: amount } : {}),
            customer_email: formData.email,
            description: `Réservation #${tempBooking.id}`,
            ...(promoValidCode ? { promo_code: promoValidCode } : {}),
          });
          setPaymentClientSecret(resp.client_secret);
          const displayAmount = (typeof amount === 'number' && promoDiscount) ? Math.max(50, Math.round(amount * (100 - promoDiscount) / 100)) : (typeof amount === 'number' ? amount : null);
          setPaymentAmountCents(displayAmount);
          setCurrentStep('payment');
        } catch (e: any) {
          const message = e?.message || "Erreur lors de l'initialisation du paiement";
          setPaymentError(message);
        }
      } catch (error) {
        console.error('Erreur lors de la finalisation:', error);
        setGeneralError('Erreur lors de la finalisation de la réservation');
      } finally {
        setIsProcessingBooking(false);
      }
    } else {
      // Retour au début si non éligible - pas de réservation à supprimer
      setCurrentStep('service');
      setFormData(null);
    }
  };

  // Affiche l'écran de chargement immédiatement si on est en train de confirmer un paiement post-redirection
  if (isConfirmingPayment) {
    return (
      <div className="pt-12 pb-16 min-h-screen flex items-center justify-center bg-neutral-offwhite">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto"
        >
          <div className="bg-white rounded-3xl shadow-lg p-10 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-accent-rose rounded-full mb-6">
              <motion.div
                className="h-10 w-10 rounded-full border-4 border-brand border-t-transparent"
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
              />
            </div>
            <h2 className="font-playfair text-3xl font-bold text-neutral-ink mb-4">
              Validation du paiement…
            </h2>
            <p className="font-nunito text-neutral-body text-lg mb-2">
              Nous finalisons votre réservation et vérifions la confirmation de paiement.
            </p>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-16 bg-neutral-offwhite">
      <SEOHead
        title="Réserver un Soin"
        description="Réservez votre massage prénatal, spa bébé ou bain enveloppé à Geneuille (25). Prise de rendez-vous en ligne — Doux ce Moman."
        path="/reservation"
      />
      {/* Error notification */}
      <AnimatePresence>
        {generalError && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 max-w-md w-full mx-4"
          >
            <div className="bg-red-50 border-l-4 border-red-500 rounded-lg shadow-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start">
                  <svg className="w-6 h-6 text-red-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <h3 className="text-red-800 font-semibold mb-1">Erreur</h3>
                    <p className="text-red-700 text-sm">{generalError}</p>
                  </div>
                </div>
                <button
                  onClick={() => setGeneralError(null)}
                  className="text-red-500 hover:text-red-700 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <AnimatePresence>
        {isProcessingBooking && (
          <motion.div
            className="fixed inset-0 z-40 flex items-center justify-center bg-neutral-ink/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-xl p-8 flex flex-col items-center gap-4 text-center"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <div className="h-10 w-10 rounded-full border-4 border-brand border-t-transparent animate-spin" />
              <h2 className="font-playfair text-2xl text-neutral-ink">Traitement de votre réservation…</h2>
              <p className="font-nunito text-neutral-body max-w-sm">
                Merci de patienter quelques instants pendant que nous préparons le paiement sécurisé.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="container mx-auto px-4">
        <div aria-live="polite" role="status" className="sr-only">{ariaMessage}</div>
        {/* Hero Section */}
        {currentStep !== 'questionnaire' && currentStep !== 'questionnaire-result' && currentStep !== 'confirmation' && (
          <div className="text-center mb-12">
            <h1 className="font-playfair text-4xl md:text-5xl font-bold text-neutral-ink mb-6">
              Réservation
            </h1>
            <p className="font-nunito text-neutral-body max-w-2xl mx-auto text-lg">
              Prenez rendez-vous pour l'un de nos services et offrez-vous un moment de détente et de bien-être.
            </p>
          </div>
        )}

        {/* Formulaire de réservation */}
        {currentStep === 'service' && (
          <div className="max-w-4xl mx-auto">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            
            {/* Section Bon Cadeau */}
            <div className="bg-gradient-to-r from-accent-rose/20 to-accent-sand/20 rounded-2xl p-6 border border-brand/30 mb-8">
              <button
                type="button"
                onClick={() => setIsGiftVoucherExpanded(!isGiftVoucherExpanded)}
                className="w-full flex items-center justify-center py-2 hover:opacity-80 transition-opacity"
              >
                <GiftIcon className="text-brand mr-3" size={24} />
                <h3 className="font-playfair font-bold text-xl text-neutral-ink">
                  Vous avez un bon cadeau ?
                </h3>
                <ChevronRight 
                  className={`ml-3 text-brand transition-transform duration-300 ${
                    isGiftVoucherExpanded ? 'rotate-90' : ''
                  }`} 
                  size={20} 
                />
              </button>
              
              <AnimatePresence>
                {isGiftVoucherExpanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="flex flex-col md:flex-row gap-4 items-center max-w-2xl mx-auto mb-4">
                <div className="flex-1">
                  <input
                    type="text"
                    value={giftVoucherNumber}
                    onChange={(e) => setGiftVoucherNumber(e.target.value.toUpperCase())}
                    className="w-full p-3 border-2 border-neutral-line rounded-xl font-nunito focus:border-brand focus:outline-none"
                    placeholder="XXXX-XXXX-XXXX"
                  />
                </div>
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleGiftVoucherCheck}
                  disabled={!giftVoucherNumber.trim() || giftVoucherLoading}
                  className="whitespace-nowrap"
                >
                  {giftVoucherLoading ? 'Vérification...' : 'Vérifier'}
                </Button>
              </div>
              
              {giftVoucherError && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl text-center"
                >
                  <p className="text-red-700 text-sm font-semibold">{giftVoucherError}</p>
                </motion.div>
              )}
              
              {giftVoucherInfo && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl"
                >
                  <div className="flex items-center space-x-2 mb-2">
                    <GiftIcon className="text-green-600" size={16} />
                    <span className="font-semibold text-green-800">
                      Bon cadeau valide !
                    </span>
                  </div>
                  <p className="text-green-700 text-sm">
                    Service pré-sélectionné : <strong>{giftVoucherInfo.name}</strong>
                    {giftVoucherInfo.category_name && ` (${giftVoucherInfo.category_name})`}
                  </p>
                </motion.div>
              )}

                    <div className="text-center mt-4 pt-4 border-t border-neutral-line">
                      <Button
                        variant="secondary"
                        to="/bons-cadeaux"
                        className="inline-flex items-center text-sm"
                      >
                        <GiftIcon className="mr-2" size={14} />
                        Acheter un bon cadeau
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Sélection de la catégorie */}
            {!giftVoucherInfo && (
              <>
                <div data-section="category-selection" className="space-y-4">
              <label className="block font-playfair font-bold text-neutral-ink mb-6 text-center text-2xl">
                Choisissez votre catégorie de soin
              </label>
              <div key={categoryUpdateKey} className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {categoriesLoading ? (
                  <div className="col-span-full text-center py-8">
                    <p className="text-neutral-body">Chargement des catégories...</p>
                  </div>
                ) : categories.length === 0 ? (
                  <div className="col-span-full text-center py-8">
                    <p className="text-neutral-body">Aucune catégorie disponible pour le moment.</p>
                  </div>
                ) : (
                  categories.map((category) => (
                    <motion.div
                    key={category.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <button
                      type="button"
                      onClick={() => handleCategorySelect(category.slug)}
                      disabled={!!giftVoucherInfo}
                      className={`w-full h-32 relative overflow-hidden rounded-xl transition-all duration-200 group border-2
                        ${selectedCategorySlug === category.slug 
                          ? 'bg-brand text-neutral-offwhite ring-2 ring-brand shadow-lg border-brand' 
                          : 'bg-white border-neutral-line hover:border-brand hover:bg-brand/10'
                        }`}
                    >
                      <div className="absolute inset-0 flex flex-col justify-center items-center p-4">
                        <div className="flex items-center mb-2">
                          <Heart className={`mr-2 ${selectedCategorySlug === category.slug ? 'text-neutral-offwhite' : 'text-brand'}`} size={24} />
                          <h3 className={`font-playfair font-bold text-lg ${selectedCategorySlug === category.slug ? 'text-neutral-offwhite' : 'text-neutral-ink'}`}>{category.name}</h3>
                        </div>
                        <p className={`font-nunito text-sm text-center ${selectedCategorySlug === category.slug ? 'text-neutral-offwhite/90' : 'text-neutral-body'}`}>{category.description}</p>
                        <ChevronRight 
                          className={`absolute right-4 top-1/2 -translate-y-1/2 transition-all duration-300 
                            ${selectedCategorySlug === category.slug 
                              ? 'translate-x-0 opacity-100 text-neutral-offwhite' 
                              : 'translate-x-2 opacity-60 text-brand'
                            }`} 
                          size={20}
                        />
                      </div>
                    </button>
                    </motion.div>
                  ))
                )}
              </div>
            </div>

            {/* Liste des services */}
            <AnimatePresence>
              {showServices && selectedCategorySlug && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  data-section="services"
                  className="space-y-4"
                >
                  <h3 className="font-playfair font-bold text-2xl text-center text-neutral-ink mb-6">
                    Sélectionnez votre soin
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {servicesLoading ? (
                      <div className="col-span-full text-center py-8">
                        <p className="text-neutral-body">Chargement des services...</p>
                      </div>
                    ) : services.length === 0 ? (
                      <div className="col-span-full text-center py-8">
                        <p className="text-neutral-body">Aucun service disponible dans cette catégorie.</p>
                      </div>
                    ) : (
                      services.map((service) => (
                        <motion.label
                          key={service.id}
                          className={`
                            relative border-2 rounded-xl p-6 transition-all duration-200
                            ${giftVoucherInfo ? 'cursor-not-allowed bg-neutral-offwhite' : 'cursor-pointer'}
                            ${watch('service') === service.id.toString()
                              ? 'bg-brand text-neutral-offwhite ring-2 ring-brand shadow-lg border-brand'
                              : 'bg-white border-neutral-line hover:border-brand hover:bg-brand/10'
                            }
                          `}
                          whileHover={!giftVoucherInfo ? { scale: 1.02 } : {}}
                          whileTap={!giftVoucherInfo ? { scale: 0.98 } : {}}
                        >
                          <input
                            type="radio"
                            value={service.id.toString()}
                            {...register('service', {
                              onChange: () => {
                                if (!giftVoucherInfo) handleServiceSelect();
                              }
                            })}
                            disabled={!!giftVoucherInfo}
                            className="sr-only"
                          />
                          <div className="flex flex-col h-full">
                            <span className={`font-playfair font-bold text-lg mb-2 ${watch('service') === service.id.toString() ? 'text-neutral-offwhite' : 'text-neutral-ink'}`}>{service.name}</span>
                            <div className={`flex items-center space-x-4 text-sm mb-2 ${watch('service') === service.id.toString() ? 'text-neutral-offwhite/90' : 'text-neutral-body'}`}>
                              <div className="flex items-center">
                                <Clock size={16} className="mr-1" />
                                <span>{service.duration_minutes} min</span>
                              </div>
                              <div className="flex items-center">
                                <Euro size={16} className="mr-1" />
                                <span>{service.price ? (service.price / 100).toFixed(0) : '0'}€</span>
                              </div>
                              {service.sessions_count && service.sessions_count > 1 && (
                                <div className="flex items-center">
                                  <Calendar size={16} className="mr-1" />
                                  <span>{service.sessions_count} séances</span>
                                </div>
                              )}
                            </div>
                            <p className={`text-sm mb-4 whitespace-pre-line ${watch('service') === service.id.toString() ? 'text-neutral-offwhite/80' : 'text-neutral-body'}`}>{service.description}</p>
                          </div>
                        </motion.label>
                      ))
                    )}
                  </div>
                  {errors.service && (
                    <p className="text-red-500 text-sm text-center">{errors.service.message}</p>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
              </>
            )}

            {selectedService && (
              <motion.div
                key={`timeslot-${selectedService}-${forceUpdate}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-8"
              >
                {/* Sélection de créneaux */}
                <div data-section="datetime">
                  <Controller
                    control={control}
                    name="datetime"
                    render={({ field }) => (
                      <TimeSlotSelector
                        serviceId={selectedService ? parseInt(selectedService) : null}
                        selectedDateTime={field.value}
                        onDateTimeSelect={(date) => {
                          field.onChange(date);
                          handleDateSelected();
                        }}
                        className="w-full"
                      />
                    )}
                  />
                  {errors.datetime && (
                    <p className="text-red-500 text-sm mt-1">{errors.datetime.message}</p>
                  )}
                </div>

                {/* Informations personnelles */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block font-playfair font-bold text-neutral-ink mb-2">
                      Prénom
                    </label>
                    <input
                      type="text"
                      {...register('firstName')}
                      className="w-full p-3 border-2 border-neutral-line rounded-xl font-nunito focus:border-brand focus:outline-none"
                      placeholder="Votre prénom"
                    />
                    {errors.firstName && (
                      <p className="text-red-500 text-sm mt-1">{errors.firstName.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block font-playfair font-bold text-neutral-ink mb-2">
                      Nom
                    </label>
                    <input
                      type="text"
                      {...register('lastName')}
                      className="w-full p-3 border-2 border-neutral-line rounded-xl font-nunito focus:border-brand focus:outline-none"
                      placeholder="Votre nom"
                    />
                    {errors.lastName && (
                      <p className="text-red-500 text-sm mt-1">{errors.lastName.message}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block font-playfair font-bold text-neutral-ink mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      {...register('email')}
                      className="w-full p-3 border-2 border-neutral-line rounded-xl font-nunito focus:border-brand focus:outline-none"
                      placeholder="votre@email.com"
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block font-playfair font-bold text-neutral-ink mb-2">
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      {...register('phone')}
                      className="w-full p-3 border-2 border-neutral-line rounded-xl font-nunito focus:border-brand focus:outline-none"
                      placeholder="06 12 34 56 78"
                    />
                    {errors.phone && (
                      <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>
                    )}
                  </div>
                </div>

                {/* Section Adresse */}
                <div className="mt-8 pt-8 border-t border-neutral-line">
                  <h4 className="font-playfair text-lg font-semibold text-neutral-ink mb-4">
                    Votre adresse
                  </h4>
                  <div className="grid grid-cols-1 gap-6">
                    <div>
                      <label className="block font-playfair font-bold text-neutral-ink mb-2">
                        Adresse
                      </label>
                      <input
                        type="text"
                        {...register('address_line1')}
                        className="w-full p-3 border-2 border-neutral-line rounded-xl font-nunito focus:border-brand focus:outline-none"
                        placeholder="Numéro et nom de rue"
                      />
                      {errors.address_line1 && (
                        <p className="text-red-500 text-sm mt-1">{errors.address_line1.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block font-playfair font-bold text-neutral-ink mb-2">
                        Complément d'adresse (optionnel)
                      </label>
                      <input
                        type="text"
                        {...register('address_line2')}
                        className="w-full p-3 border-2 border-neutral-line rounded-xl font-nunito focus:border-brand focus:outline-none"
                        placeholder="Appartement, bâtiment, etc. (optionnel)"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block font-playfair font-bold text-neutral-ink mb-2">
                          Code postal
                        </label>
                        <input
                          type="text"
                          {...register('postal_code')}
                          maxLength={5}
                          className="w-full p-3 border-2 border-neutral-line rounded-xl font-nunito focus:border-brand focus:outline-none"
                          placeholder="75001"
                        />
                        {errors.postal_code && (
                          <p className="text-red-500 text-sm mt-1">{errors.postal_code.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block font-playfair font-bold text-neutral-ink mb-2">
                          Ville
                        </label>
                        <input
                          type="text"
                          {...register('city')}
                          className="w-full p-3 border-2 border-neutral-line rounded-xl font-nunito focus:border-brand focus:outline-none"
                          placeholder="Paris"
                        />
                        {errors.city && (
                          <p className="text-red-500 text-sm mt-1">{errors.city.message}</p>
                        )}
                      </div>
                    </div>

                    <input type="hidden" {...register('country')} value="France" />
                  </div>
                </div>

                {/* Champs conditionnels selon le service */}
                {effectiveServiceData?.category?.slug === 'prenatal' && (
                  <div>
                    <label className="block font-playfair font-bold text-neutral-ink mb-2">
                      Semaine de grossesse (optionnel)
                    </label>
                    <select
                      {...register('pregnancy_week')}
                      className="w-full p-3 border-2 border-neutral-line rounded-xl font-nunito focus:border-brand focus:outline-none"
                    >
                      <option value="">Sélectionnez votre semaine de grossesse</option>
                      {Array.from({ length: 37 }, (_, i) => i + 14).map((week) => (
                        <option key={week} value={week}>Semaine {week}</option>
                      ))}
                    </select>
                  </div>
                )}

                {(effectiveServiceData?.category?.slug === 'spa' || effectiveServiceData?.category?.slug === 'bebe') && (
                  <div>
                    <label className="block font-playfair font-bold text-neutral-ink mb-2">
                      Âge du bébé (optionnel)
                    </label>
                    <select
                      {...register('baby_age')}
                      className="w-full p-3 border-2 border-neutral-line rounded-xl font-nunito focus:border-brand focus:outline-none"
                    >
                      <option value="">Sélectionnez l'âge de votre bébé</option>
                      {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                        <option key={month} value={month}>{month} mois</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Notes */}
                <div>
                  <label className="block font-playfair font-bold text-neutral-ink mb-1">
                    Notes ou informations complémentaires (optionnel)
                  </label>
                  <p className="font-nunito text-sm text-neutral-body/80 mb-2">
                    Ex. : mois de grossesse, prénom et date de naissance de votre bébé…
                  </p>
                  <textarea
                    {...register('notes')}
                    className="w-full p-3 border-2 border-neutral-line rounded-xl font-nunito focus:border-brand focus:outline-none"
                    rows={4}
                  />
                </div>

                {/* Code promo — masqué si bon cadeau utilisé */}
                {!(useGiftVoucherFlag && giftVoucherInfo) && effectiveServiceData?.price && (
                  <div className="bg-neutral-offwhite rounded-xl p-4 border border-neutral-line">
                    <label className="block font-playfair font-bold text-neutral-ink mb-2">
                      Code promotionnel (optionnel)
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={promoCode}
                        onChange={(e) => {
                          setPromoCode(e.target.value.toUpperCase());
                          if (promoValidCode) {
                            setPromoValidCode(null);
                            setPromoDiscount(null);
                            setPromoError(null);
                          }
                        }}
                        disabled={!!promoValidCode}
                        className="flex-1 p-3 border-2 border-neutral-line rounded-xl font-nunito focus:border-brand focus:outline-none uppercase tracking-wider disabled:bg-gray-100"
                        placeholder="Ex: BIENVENUE20"
                      />
                      {promoValidCode ? (
                        <button
                          type="button"
                          onClick={() => {
                            setPromoCode('');
                            setPromoValidCode(null);
                            setPromoDiscount(null);
                            setPromoError(null);
                          }}
                          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-xl font-nunito hover:bg-gray-300 transition-colors"
                        >
                          Retirer
                        </button>
                      ) : (
                        <button
                          type="button"
                          disabled={!promoCode.trim() || promoLoading}
                          onClick={async () => {
                            setPromoLoading(true);
                            setPromoError(null);
                            try {
                              const result = await promoCodeService.validate(promoCode);
                              if (result.valid && result.discount_percent) {
                                setPromoDiscount(result.discount_percent);
                                setPromoValidCode(result.code || promoCode);
                              } else {
                                setPromoError(result.error || 'Code invalide');
                              }
                            } catch (e: any) {
                              setPromoError(e.message || 'Erreur de validation');
                            } finally {
                              setPromoLoading(false);
                            }
                          }}
                          className="px-4 py-2 bg-brand text-white rounded-xl font-nunito hover:bg-brand/90 transition-colors disabled:opacity-50"
                        >
                          {promoLoading ? '...' : 'Appliquer'}
                        </button>
                      )}
                    </div>
                    {promoError && (
                      <p className="text-red-500 text-sm mt-2">{promoError}</p>
                    )}
                    {promoValidCode && promoDiscount && (
                      <p className="text-green-600 text-sm mt-2 font-medium">
                        Code {promoValidCode} appliqué : -{promoDiscount}% de réduction
                      </p>
                    )}
                  </div>
                )}

                {/* Conditions générales */}
                <div className="flex items-start space-x-2">
                  <div className="flex items-center h-5">
                    <input
                      type="checkbox"
                      {...register('terms')}
                      id="terms"
                      className="h-4 w-4 rounded border-neutral-line text-brand focus:ring-brand"
                    />
                  </div>
                  <label 
                    htmlFor="terms" 
                    className="font-nunito text-neutral-body cursor-pointer select-none"
                  >
                    J'accepte les conditions générales et la politique de confidentialité
                  </label>
                </div>
                {errors.terms && (
                  <p className="text-red-500 text-sm">{errors.terms.message}</p>
                )}


                {/* Prix et durée */}
                {effectiveServiceData && (
                  <div className="bg-brand/10 rounded-xl p-4">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center">
                          <Clock className="text-brand mr-2" size={20} />
                          <span className="font-nunito">
                            {effectiveServiceData.duration_minutes ? 
                              `${effectiveServiceData.duration_minutes} minutes` : 
                              'Durée à confirmer'
                            }
                          </span>
                        </div>
                        <div className="flex items-center">
                          <Euro className="text-brand mr-2" size={20} />
                          <span className="font-nunito">
                            {useGiftVoucherFlag && giftVoucherInfo ? 
                              'Offert' : 
                              effectiveServiceData.price ? (
                                promoDiscount ? (
                                  <>
                                    <span className="line-through text-neutral-body mr-2">{(effectiveServiceData.price / 100).toFixed(0)}€</span>
                                    <span className="text-green-600 font-bold">{(Math.max(50, Math.round(effectiveServiceData.price * (100 - promoDiscount) / 100)) / 100).toFixed(0)}€</span>
                                  </>
                                ) : `${(effectiveServiceData.price / 100).toFixed(0)}€`
                              ) : 'Prix à confirmer'
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Bouton de soumission */}
                <div className="text-center">
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    disabled={isSubmitting}
                    className="w-full md:w-auto"
                  >
                    {isSubmitting ? 'Envoi en cours...' : (questionnaire ? 'Continuer vers le questionnaire' : 'Confirmer la réservation')}
                  </Button>
                </div>
              </motion.div>
            )}
          </form>
          </div>
        )}



        {/* Questionnaire */}
        {currentStep === 'questionnaire' && questionnaire && (
          <QuestionnaireForm
            questionnaire={questionnaire}
            onSubmit={handleQuestionnaireSubmit}
            onCancel={() => {
              setCurrentStep('service');
              setFormData(null);
            }}
            isSubmitting={isSubmitting}
          />
        )}

        {/* Fallback si questionnaire requis mais pas chargé */}
        {currentStep === 'questionnaire' && !questionnaire && (
          <div className="max-w-2xl mx-auto text-center py-12">
            <p className="text-neutral-body">Chargement du questionnaire...</p>
          </div>
        )}

        {/* Résultat du questionnaire */}
        {currentStep === 'questionnaire-result' && (localValidation || validation) && (
          <div data-section="questionnaire-result" className="scroll-mt-24 md:scroll-mt-28">
            <QuestionnaireResult
              validation={localValidation || validation}
              serviceName={effectiveServiceData?.name}
              onContinue={handleQuestionnaireResult}
              onCancel={() => {
                setCurrentStep('service');
                setFormData(null);
              }}
            />
          </div>
        )}

        {/* Page de validation du paiement */}
        {currentStep === 'payment-processing' && (
          <div className="pt-12 pb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-2xl mx-auto"
            >
              <div className="bg-white rounded-3xl shadow-lg p-10 text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-accent-rose rounded-full mb-6">
                  <motion.div
                    className="h-10 w-10 rounded-full border-4 border-brand border-t-transparent"
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                  />
                </div>
                <h2 className="font-playfair text-3xl font-bold text-neutral-ink mb-4">
                  Validation du paiement…
                </h2>
                <p className="font-nunito text-neutral-body text-lg mb-2">
                  Nous finalisons votre réservation et vérifions la confirmation Stripe.
                </p>
                <p className="font-nunito text-neutral-body text-sm text-neutral-body/80">
                  Cette étape peut prendre quelques secondes. Merci de ne pas fermer cette page.
                </p>
              </div>
            </motion.div>
          </div>
        )}

        {/* Étape de paiement */}
        {currentStep === 'payment' && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
              <h2 className="font-playfair text-2xl font-bold text-neutral-ink mb-4 text-center">Paiement sécurisé</h2>
              {paymentAmountCents !== null && (
                <div className="flex items-center justify-center mb-6 bg-brand/10 py-3 px-4 rounded-xl">
                  <p className="text-center text-neutral-ink">
                    <span className="text-neutral-body mr-2">Montant à régler:</span>
                    <span className="font-bold text-xl text-brand">{(paymentAmountCents / 100).toFixed(2)} €</span>
                  </p>
                </div>
              )}
              {paymentError && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 bg-red-50 border-l-4 border-red-500 rounded-lg mb-6"
                  data-error="payment"
                >
                  <div className="flex items-start">
                    <svg className="w-6 h-6 text-red-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <h3 className="text-red-800 font-semibold mb-1">Erreur de paiement</h3>
                      <p className="text-red-700 text-sm">{paymentError}</p>
                      <p className="text-red-600 text-xs mt-2">Veuillez vérifier vos informations de paiement et réessayer.</p>
                    </div>
                  </div>
                </motion.div>
              )}
              {paymentClientSecret && (
                <PaymentSection
                  clientSecret={paymentClientSecret}
                  amountCents={paymentAmountCents}
                  bookingId={tempBookingId}
                  returnUrl={typeof window !== 'undefined'
                    ? `${window.location.origin}/reservation?booking_id=${tempBookingId || finalBooking?.id || ''}`
                    : undefined}
                  onSuccess={async (piId) => {
                    // Payment successful — marquer comme payé côté serveur
                    const bId = tempBookingId || finalBooking?.id;
                    if (bId && piId) {
                      try {
                        await paymentService.markBookingPaid(bId, piId);
                      } catch (e) {
                        console.warn('markBookingPaid échoué (webhook le fera):', e);
                      }
                    }
                    if (tempBookingId) {
                      await createFinalBookingWithId(tempBookingId, { showProcessing: true, fallbackStep: 'payment' });
                    } else if (finalBooking?.id) {
                      await createFinalBookingWithId(finalBooking.id, { showProcessing: true, fallbackStep: 'payment' });
                    }
                  }}
                  onError={(message) => {
                    setPaymentError(message);
                    // Scroll to error message
                    setTimeout(() => {
                      const errorElement = document.querySelector('[data-error="payment"]');
                      if (errorElement) {
                        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }
                    }, 100);
                  }}
                  onBackToBooking={() => {
                    // Retour à l'étape de réservation - l'annulation sera gérée automatiquement par PaymentSection
                    setCurrentStep('service');
                    setPaymentClientSecret(null);
                    setPaymentAmountCents(null);
                    setTempBookingId(null);
                  }}
                />
              )}
            </div>
          </div>
        )}

        {/* Finalisation de la réservation */}
        {currentStep === 'booking' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto"
          >
            <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6">
                <Heart className="text-green-600" size={32} />
              </div>
              <h2 className="font-playfair text-3xl font-bold text-neutral-ink mb-4">
                Questionnaire validé !
              </h2>
              <p className="text-neutral-body mb-8">
                Vous êtes éligible pour le service sélectionné. Vous pouvez maintenant finaliser votre réservation.
              </p>
              <Button
                variant="primary"
                size="lg"
                onClick={createFinalBooking}
                className="w-full md:w-auto"
              >
                Finaliser la réservation
              </Button>
            </div>
          </motion.div>
        )}

        {/* Page de confirmation */}
        {currentStep === 'confirmation' && finalBooking && (
          <div data-section="confirmation" className="scroll-mt-24 md:scroll-mt-28">
            <BookingConfirmation
              booking={finalBooking}
              onGoHome={() => {
                window.location.href = '/';
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Reservation;
